import type { CosmeticsInput } from '../schemas/progressionDefs';

/**
 * Non-item rewards (content-schemas §3.8, economy-progression §13–§14). Asset frames use the cosmetic ID itself
 * (style-guide §ID cosmetic). Photo mode's Polaroid frame and 4 default stickers are NOT `isDefault` for a new
 * save: unl_photo_mode grants them. Avatar hair and shirt colors are all free from the start.
 */
type StickerGroup = CosmeticsInput['stickers'][number]['group'];

const sticker = (id: `stk_${string}`, group: StickerGroup) => ({
  id,
  nameKey: `sticker.${id.slice('stk_'.length)}`,
  frame: id,
  isDefault: false,
  group,
});

export const COSMETICS = {
  titles: [
    { id: 'ttl_cozy_home_5', nameKey: 'title.cozy_home_5' },
    { id: 'ttl_cutest_shop', nameKey: 'title.cutest_shop' },
    { id: 'ttl_lan_bestie', nameKey: 'title.lan_bestie' },
    { id: 'ttl_honorary_colleague', nameKey: 'title.honorary_colleague' },
    { id: 'ttl_best_shopkeeper', nameKey: 'title.best_shopkeeper' },
    { id: 'ttl_cat_secretary', nameKey: 'title.cat_secretary' },
    { id: 'ttl_orders_10', nameKey: 'title.orders_10' },
    { id: 'ttl_studio_photographer', nameKey: 'title.studio_photographer' },
    { id: 'ttl_shoot_master', nameKey: 'title.shoot_master' },
  ],
  badges: [
    { id: 'bdg_right_spot', nameKey: 'badge.right_spot', icon: 'bdg_right_spot' },
    { id: 'bdg_seller_50', nameKey: 'badge.seller_50', icon: 'bdg_seller_50' },
    { id: 'bdg_decor_10', nameKey: 'badge.decor_10', icon: 'bdg_decor_10' },
    { id: 'bdg_set_complete', nameKey: 'badge.set_complete', icon: 'bdg_set_complete' },
  ],
  // 16 stickers = 4 default + 12 rewards (economy §13).
  stickers: [
    sticker('stk_default_1', 'default'),
    sticker('stk_default_2', 'default'),
    sticker('stk_default_3', 'default'),
    sticker('stk_default_4', 'default'),
    sticker('stk_mit_1', 'mit'),
    sticker('stk_mit_2', 'mit'),
    sticker('stk_mit_3', 'mit'),
    sticker('stk_mit_4', 'mit'),
    sticker('stk_mit_5', 'mit'),
    sticker('stk_photo_1', 'photo'),
    sticker('stk_photo_2', 'photo'),
    sticker('stk_photo_3', 'photo'),
    sticker('stk_boss_cat_approved', 'boss_cat'),
    sticker('stk_mail_1', 'mail'),
    sticker('stk_mail_2', 'mail'),
    sticker('stk_mail_3', 'mail'),
  ],
  photoFrames: [
    { id: 'frm_polaroid', nameKey: 'frame.polaroid', overlay: 'frm_polaroid', isDefault: false },
    { id: 'frm_cozy_home', nameKey: 'frame.cozy_home', overlay: 'frm_cozy_home', isDefault: false },
    { id: 'frm_home_lv5', nameKey: 'frame.home_lv5', overlay: 'frm_home_lv5', isDefault: false },
    { id: 'frm_vo_o_li', nameKey: 'frame.vo_o_li', overlay: 'frm_vo_o_li', isDefault: false },
  ],
  avatarParts: [
    { id: 'av_hair_1', slot: 'hair', nameKey: 'avatar.hair_1', frame: 'av_hair_1', isDefault: true },
    { id: 'av_hair_2', slot: 'hair', nameKey: 'avatar.hair_2', frame: 'av_hair_2', isDefault: true },
    { id: 'av_hair_3', slot: 'hair', nameKey: 'avatar.hair_3', frame: 'av_hair_3', isDefault: true },
    // One shared shirt sprite (avt_shirt) tinted with the palette colors (style-guide §2).
    {
      id: 'av_shirt_cream',
      slot: 'shirtColor',
      nameKey: 'avatar.shirt_cream',
      tint: '#FFF8EE',
      isDefault: true,
    },
    {
      id: 'av_shirt_pink',
      slot: 'shirtColor',
      nameKey: 'avatar.shirt_pink',
      tint: '#F7B6C8',
      isDefault: true,
    },
    {
      id: 'av_shirt_mint',
      slot: 'shirtColor',
      nameKey: 'avatar.shirt_mint',
      tint: '#BFE6D3',
      isDefault: true,
    },
    {
      id: 'av_shirt_lavender',
      slot: 'shirtColor',
      nameKey: 'avatar.shirt_lavender',
      tint: '#CDBDF2',
      isDefault: true,
    },
    {
      id: 'av_hat_bucket_pink',
      slot: 'hat',
      nameKey: 'avatar.hat_bucket_pink',
      frame: 'av_hat_bucket_pink',
      isDefault: true,
    },
    {
      id: 'av_hat_flower',
      slot: 'hat',
      nameKey: 'avatar.hat_flower',
      frame: 'av_hat_flower',
      isDefault: false,
    },
    {
      id: 'av_hat_cat_beanie',
      slot: 'hat',
      nameKey: 'avatar.hat_cat_beanie',
      frame: 'av_hat_cat_beanie',
      isDefault: false,
    },
  ],
  avatarFrames: [{ id: 'afr_neighbor', nameKey: 'avatar.frame.neighbor', frame: 'afr_neighbor' }],
} as const satisfies CosmeticsInput;
